import type { RequestOptions } from "@@/plugin-request/request";
import type { RequestConfig } from "@umijs/max";
import { history } from "@umijs/max";
import { message, notification } from "antd";

const loginPath = "/user/login";

/**
 * 判断是否为白名单页面
 */
const isWhiteListPath = (pathname: string): boolean => {
  const normalizedPath = pathname.toLowerCase().replace(/\/+$/, "");
  return normalizedPath.startsWith("/user") || normalizedPath === "";
};

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = "BizError";
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === "BizError") {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const status = error.response.status;
        const response = error.response.data || {};

        if (status === 401) {
          // Token过期或无效，清除本地存储
          localStorage.removeItem("token");
          localStorage.removeItem("token_expires_at");

          // 获取当前路径
          const currentPath = window.location.pathname;

          // 如果已经在登录页，不做任何处理，避免死循环
          if (isWhiteListPath(currentPath)) {
            return;
          }

          // 根据不同的错误类型显示不同的消息
          let errorMsg = "登录已过期，请重新登录";
          if (response.errorCode) {
            switch (response.errorCode) {
              case "TOKEN_MISSING":
                errorMsg = "请先登录";
                break;
              case "TOKEN_INVALID":
                errorMsg = "Token无效，请重新登录";
                break;
              case "TOKEN_EXPIRED":
                errorMsg = "Token已过期，请重新登录";
                break;
              case "ACCOUNT_DISABLED":
                errorMsg = "账户已被禁用";
                break;
              default:
                errorMsg = response.errorMessage || "登录已过期，请重新登录";
            }
          }

          message.error(errorMsg);

          // 使用 history.replace 而非 window.location.href，避免页面刷新
          history.replace(loginPath);
          return;
        }
        message.error(
          response.message ||
            response.errorMessage ||
            `Response status:${status}`
        );
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error("None response! Please retry.");
      } else {
        // 发送请求时出了点问题
        message.error("Request error, please retry.");
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const token = localStorage.getItem("token");
      // console.log("Request interceptor - URL:", config.url);
      // console.log("Request interceptor - token exists:", !!token);

      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
          token: token, // 后端也检查token头
        };
        // console.log("Request interceptor - headers set:", {
        //   Authorization: `Bearer ${token.substring(0, 20)}...`,
        //   token: token.substring(0, 20) + "...",
        // });
      } else {
        // console.log("Request interceptor - no token found");
      }
      return { ...config };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      // 注意：不在这里处理错误，让 errorThrower 和 errorHandler 统一处理
      return response;
    },
  ],
};
